import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, Card, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Skeleton, TextField, Typography} from "@mui/material";
import Button from "../../base/button/button";
import TopBarProgressIndicator from "../../app-specific/topbar-progress-indicator";
import useIsMounted from "../../../hooks/use-is-mounted";
import ImportingLottieAnimation from "../../app-specific/lottie-animations/importing";
import CreateProductForm from "../../app-specific/create-product-form";
import classnames from "classnames";
import {OpenInNew, TrendingFlat} from "@mui/icons-material";
import Api from "../../../../core/services/api/api";
import Utils from "../../../../core/services/utils/utils";
import LoadingLottieAnimation from "../../app-specific/lottie-animations/loading";
import {useCachedPropertyGroupsController} from "../../../hooks/use-industry";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Switch} from "../../base/switch";
import {Checkbox} from "../../base/checkbox/checkbox";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";
import {Link} from "react-router-dom";
import SummaryInfoCard from "../../app-specific/summary-cards/summary-info-card";
import ProductInformationPopover from "../../app-specific/draggable-popover/product-information-popover";
import useSavedComponentsState from "../../../hooks/use-saved-components-state";
import PartAutocomplete from "../../text-fields/autocomplete/part-autocomplete";

const steps = {
    createProduct: 'createProduct',
    selectProduct: 'selectProduct',
    copyProduct: 'copyProduct',
    reviewCopyResults: 'reviewCopyResults',
};

const processTabs = {
    createProduct: 'Create Product',
    selectProduct: 'Select Product',
    copyProduct: 'Copy Properties',
    reviewCopyResults: 'Review Results',
}

export const CopyProductDialogModes = {
    copyFrom: 'copyFrom',
    makeCopy: 'makeCopy',
}

const CopyProductDialog = ({open, close: closeProps, mode, providedProduct}) => {
    const [step, setStep] = useState(steps.createProduct);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [copyResults, setCopyResults] = useState(null);
    const [copyingInformation, setCopyingInformation] = useState(false);

    const copyInformationRef = useRef(null);

    const visibleProcessTabs = useMemo(() => {
        switch (mode) {
            case CopyProductDialogModes.copyFrom:
                return [processTabs.selectProduct, processTabs.copyProduct, processTabs.reviewCopyResults];
            case CopyProductDialogModes.makeCopy:
                return [processTabs.createProduct, processTabs.copyProduct, processTabs.reviewCopyResults];
            default:
                return [processTabs.createProduct, processTabs.copyProduct, processTabs.reviewCopyResults];
        }
    }, [mode]);

    const currentProcess = useMemo(() => {
        switch (step) {
            case steps.createProduct:
                return processTabs.createProduct;
            case steps.selectProduct:
                return processTabs.selectProduct;
            case steps.copyProduct:
                return processTabs.copyProduct;
            case steps.reviewCopyResults:
                return processTabs.reviewCopyResults;
            default:
                return processTabs.createProduct;
        }
    }, [step])

    const newProductFormInitialValues = useMemo(() => {
        return ({
            industry: {id: providedProduct?.industryId},
            partNo: `${providedProduct?.partNo}-COPY`,
            type: providedProduct?.type,
            category: providedProduct?.category?.id,
        });
    }, [providedProduct])

    const sourceProduct = useMemo(() => {
        switch (mode) {
            case CopyProductDialogModes.copyFrom:
                return selectedProduct
            case CopyProductDialogModes.makeCopy:
                return providedProduct;
            default:
                return null;
        }
    }, [mode, providedProduct, selectedProduct])

    const targetProduct = useMemo(() => {
        switch (mode) {
            case CopyProductDialogModes.copyFrom:
                return providedProduct;
            case CopyProductDialogModes.makeCopy:
                return selectedProduct;
            default:
                return null;
        }
    }, [mode, providedProduct, selectedProduct])

    /**
     * Closes the dialog
     * @type {(function(): void)|*}
     */
    const close = useCallback(() => {
        closeProps(!!copyResults && targetProduct?.id === providedProduct?.id);
    }, [closeProps, copyResults, providedProduct?.id, targetProduct?.id])

    /**
     * Resets the state of the dialog
     * @type {(function(): void)}
     */
    const resetState = useCallback(() => {
        setSelectedProduct(null);
        setCopyingInformation(false);
        setCopyResults(null);
    }, [])

    /**
     * with each change in the [open] state of the dialog:
     * - if closed, resets the applied payments, credits and the step.
     */
    useEffect(() => {
        if (!open)
            return
        resetState();
        switch (mode) {
            case CopyProductDialogModes.copyFrom:
                setStep(steps.selectProduct);
                break;
            case CopyProductDialogModes.makeCopy:
                setStep(steps.createProduct);
                break;
            default:
                break;
        }
    }, [open, mode, resetState])


    /**
     * Determines if a ball has been passed over relative to their process of adding multi invoices
     * @param currentType {string}
     * @param type {string}
     * @return {boolean}
     */
    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case processTabs.createProduct:
            case processTabs.selectProduct:
            default:
                return false
            case processTabs.copyProduct:
                return (type === processTabs.createProduct || type === processTabs.selectProduct);
            case processTabs.reviewCopyResults:
                return (type === processTabs.createProduct || type === processTabs.selectProduct || type === processTabs.copyProduct);
        }
    }, [])

    const dialogSize = useMemo(() => {
        switch (step) {
            case steps.createProduct:
            case steps.selectProduct:
                return 'sm';
            case steps.copyProduct:
            case steps.reviewCopyResults:
                return 'md';
            default:
                return 'sm';
        }
    }, [step])

    const dialogActions = useMemo(() => {
        switch (step) {
            case steps.createProduct:
                return {
                    show: true,
                    left: [{
                        onClick: () => close(),
                        text: "Cancel",
                    }],
                    right: [],
                };
            case steps.selectProduct:
                return {
                    show: true,
                    left: [{
                        onClick: () => close(),
                        text: "Cancel",
                    }],
                    right: [
                        ...(!!selectedProduct ? [{
                            onClick: () => setStep(steps.copyProduct),
                            text: "Proceed with Copy",
                        }] : []),
                    ]
                };
            case steps.copyProduct:
                return {
                    show: true,
                    left: [
                        ...(CopyProductDialogModes.copyFrom === mode ? [{
                            onClick: () => setStep(steps.selectProduct),
                            text: "Back",
                        }] : [])
                    ],
                    right: [
                        {
                            onClick: () => copyInformationRef.current?.submit(),
                            disabled: copyingInformation,
                            text: copyingInformation ? 'Copying Information' : "Copy Information",
                        }
                    ]
                };
            default:
                return {
                    show: false,
                }
        }
    }, [step, selectedProduct, mode, copyingInformation, close])

    /**
     * Handles the creation of a product
     * @type {(function(*): void)}
     */
    const onProductCreated = useCallback((product) => {
        setSelectedProduct(product);
        setStep(steps.copyProduct);
    }, []);

    /**
     * Handles the selection of a product
     * @type {(function(*): void)}
     */
    const onProductSelected = useCallback((product) => {
        setSelectedProduct(product);
        setStep(steps.copyProduct);
    }, [])

    /**
     * Handles the completion of copying information
     * @type {(function(*): void)}
     */
    const onInformationCopied = useCallback((copyResults) => {
        setStep(steps.reviewCopyResults);
        setCopyResults(copyResults);
    }, [])

    return (
        <Dialog
            open={open}
            setOpen={() => close()}
            maxWidth={dialogSize}
            className={'bound-dialog copy-product-dialog'}
        >
            <DialogTitle>
                <TopBarProgressIndicator
                    currentType={currentProcess}
                    types={visibleProcessTabs}
                    passed={passed}
                />
            </DialogTitle>
            <DialogContent className={'mt--3 pb-4'}>
                {
                    step === steps.createProduct &&
                    <CreateProductForm
                        className={'px-4 mb-3'}
                        onProductCreated={onProductCreated}
                        initialValues={newProductFormInitialValues}
                        terminate={close}
                    />
                }
                {
                    step === steps.selectProduct &&
                    <SelectProductForm
                        onProductSelected={onProductSelected}
                        selectedProduct={selectedProduct}
                        existingProductId={providedProduct?.id}
                        existingProductIndustryId={providedProduct?.industryId}
                        className={'px-4 mb-3'}
                    />
                }
                {
                    step === steps.copyProduct &&
                    <CopyProductForm
                        ref={copyInformationRef}
                        sourceProduct={sourceProduct}
                        targetProduct={targetProduct}
                        setCopyingInformation={setCopyingInformation}
                        onInformationCopied={onInformationCopied}
                        industryId={providedProduct?.industryId}
                        mode={mode}
                        className={'px-4 mb-3'}
                    />
                }
                {
                    step === steps.reviewCopyResults &&
                    <ReviewResults
                        copyResults={copyResults}
                        sourceProduct={sourceProduct}
                        targetProduct={targetProduct}
                        className={'px-4 mb-3'}
                        close={close}
                    />
                }
            </DialogContent>
            {
                dialogActions.show &&
                <DialogActions className={'p-0 w-100 d-flex flex-column'}>
                    <Divider className='divider'/>
                    <div className={'d-flex flex-wrap w-100 justify-content-between align-items-center m-0'}>
                        <div className={'d-flex align-items-center justify-content-start flex-grow-1'}>
                            {
                                !!dialogActions.left.length &&
                                dialogActions.left.map((action, i, array) => (
                                    <Button
                                        key={i}
                                        className='px-4 py-2-5'
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={action.onClick}
                                        disabled={!!action.disabled}
                                        borderRadius={`0 ${i === array.length - 1 ? '10px' : '0'} 0 ${i === 0 ? '10px' : '0'}`}
                                    >
                                        {action.text ?? ''}
                                    </Button>
                                ))
                            }
                        </div>
                        <div className={'d-flex align-items-center justify-content-end flex-grow-1'}>
                            {
                                !!dialogActions.right.length &&
                                dialogActions.right.map((action, i, array) => (
                                    <Button
                                        key={i}
                                        className=' px-4 py-2-5'
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={action.onClick}
                                        disabled={!!action.disabled}
                                        borderRadius={`${i === 0 ? '10px' : '0'} 0 ${i === array.length - 1 ? '10px' : '0'} 0`}
                                    >
                                        {action.text ?? ''}
                                    </Button>
                                ))
                            }
                        </div>
                    </div>
                </DialogActions>
            }
        </Dialog>
    );
}

const SelectProductForm = ({onProductSelected, className, existingProductId, selectedProduct, existingProductIndustryId}) => {

    /**
     * Callback to handle the selection of a product
     * @type {(function(*): void)}
     */
    const addProduct = useCallback((product) => {
        onProductSelected({
            id: product.id,
            partNo: product.partNo,
        })
    }, [onProductSelected])

    return (
        <>
            <div className={classnames(className, 'd-flex flex-column')}>
                <Typography variant={'body2'} className={'mb-3'}>
                    You can select a product from the list below to copy its information to a new product.
                </Typography>
                <PartAutocomplete
                    clearOnBlur
                    fullWidth
                    label={"New Product"}
                    value={selectedProduct}
                    onChange={(_, value) => value && addProduct(value)}
                    getOptionLabel={v => v?.partNo ?? v?.partNumber ?? ''}
                    industryId={existingProductIndustryId}
                    filterOptions={(options) => options.filter(e => e.id !== existingProductId)}
                />
            </div>
        </>
    );
}

const copyProductFormInitialState = {
    propertiesIds: [],
    industryPropertyIds: [],
    price: {
        apply: false,
        isPercentage: false,
        value: null,
    },
    relation: {
        apply: false,
        relationType: null,
    },
    hardCopy: false,

}


const CopyProductForm = forwardRef(({
                                        sourceProduct,
                                        targetProduct,
                                        setCopyingInformation: setCopyingInformationProps,
                                        onInformationCopied,
                                        className,
                                        industryId,
                                        mode,
                                    }, ref) => {
    const {highestVersion: getCachedState, save: saveCachedState} = useSavedComponentsState({name: "copy_product_dialog__default_state", version: '1.0.0'});
    const {highestVersion: getCachedIndustrySpecificState, save: saveCachedIndustrySpecificState} = useSavedComponentsState({name: `CPDDSI_${industryId}`, version: '1.0.0'});
    const [loadingState, setLoadingState] = useState(true);
    const [loadingProperties, setLoadingProperties] = useState(true);
    const [properties, setProperties] = useState([]);
    const [loadingRelationTypes, setLoadingRelationTypes] = useState(false);
    const [relationTypes, setRelationTypes] = useState(null);
    const [state, setState] = useState(copyProductFormInitialState);
    const [copyingInformation, _setCopyingInformation] = useState(false);
    const [hasDefaultSettings, setHasDefaultSettings] = useState(false);
    const {getPropertyGroupsForIndustry} = useCachedPropertyGroupsController()
    const isMounted = useIsMounted();

    /**
     * Sets the copying information state
     * @type {(function(*): void)}
     */
    const setCopyingInformation = useCallback((val) => {
        _setCopyingInformation(val);
        setCopyingInformationProps(val);
    }, [setCopyingInformationProps])

    /**
     * Saves the default settings for the copy product dialog
     * @type {(function(): void)}
     */
    const saveDefaultSettings = useCallback(() => {
        void Promise.all([
            saveCachedState({
                propertiesIds: state.propertiesIds,
                price: state.price,
                relation: state.relation,
                hardCopy: state.hardCopy,
            }),
            saveCachedIndustrySpecificState({
                industryPropertyIds: state.industryPropertyIds,
            }),
        ])
    }, [saveCachedState, state.propertiesIds, state.price, state.relation, state.hardCopy, state.industryPropertyIds, saveCachedIndustrySpecificState])

    /**
     * Copies the information from the source product to the target product
     * @type {(function(): Promise<void>)}
     */
    const copyInformation = useCallback(async () => {
        if (hasDefaultSettings) {
            void saveDefaultSettings();
        }
        setCopyingInformation(true);
        const forApi = {
            sourcePartId: sourceProduct?.id,
            targetPartId: targetProduct?.id,
            partInformationSectionIds: state.propertiesIds,
            industryGroupPropertyIds: state.industryPropertyIds,
            priceConfig: {
                copyPrice: state.price.apply,
                changeAmount: state.price.isPercentage ? 0 : state.price.value,
                changePercentage: state.price.isPercentage ? state.price.value : 0,
            },
            relationConfig: {
                markInRelated: state.relation.apply,
                partRelationTypeId: state.relation.relationType?.id,
            },
            hardCopy: mode === CopyProductDialogModes.makeCopy ? false : state.hardCopy,
        }
        const response = await Api.productCopyFromPart(forApi);
        if (!isMounted())
            return;
        setCopyingInformation(false);
        if (response?.resultFlag) {
            onInformationCopied(response?.data ?? {});
        }
    }, [saveDefaultSettings, setCopyingInformation, sourceProduct?.id, targetProduct?.id, state.propertiesIds, state.industryPropertyIds, state.price.apply, state.price.isPercentage, state.price.value, state.relation.apply, state.relation.relationType?.id, state.hardCopy, mode, isMounted, onInformationCopied, hasDefaultSettings])

    /**
     * Exposes the submit function to the parent component
     */
    useImperativeHandle(ref, () => ({
        submit: copyInformation,
    }), [copyInformation]);

    /**
     * Fetches the default settings for the copy product dialog
     * @type {(function(): Promise<void>)}
     */
    const getDefaultSettings = useCallback(async () => {
        setLoadingState(true);
        const cachedState = await getCachedState();
        const cachedIndustrySpecificState = await getCachedIndustrySpecificState();
        if (!isMounted())
            return;
        const hasDefaultSettings = !!cachedState?.data || !!cachedIndustrySpecificState?.data;
        setState(prevState => Utils.deepMerge(
            {},
            prevState,
            cachedState?.data ?? {},
            cachedIndustrySpecificState?.data ?? {},
        ));
        setHasDefaultSettings(hasDefaultSettings);
        setLoadingState(false);
    }, [getCachedIndustrySpecificState, getCachedState, isMounted])

    /**
     * As soon as the component mounts, fetch the default settings
     */
    useEffect(() => {
        void getDefaultSettings();
    }, [getDefaultSettings])

    /**
     * Fetches the properties to copy
     * @type {(function(): Promise<void>)}
     */
    const getProperties = useCallback(async () => {
        setLoadingProperties(true);
        const [propertyGroups, propertiesResponse] = await Promise.all([
            getPropertyGroupsForIndustry(industryId),
            Api.getPartSections(),
        ])
        if (!isMounted())
            return;
        setLoadingProperties(false);
        const properties = [];
        if (propertiesResponse?.resultFlag) {
            properties.push(...propertiesResponse?.data.map(e => ({
                id: e.id,
                title: e.title,
                industryBased: false,
            })))
        }
        if (!!propertyGroups?.length) {
            properties.push(...propertyGroups.map(e => ({
                id: e.id,
                title: e.title,
                industryBased: true,
            })))
        }
        setProperties(properties);
    }, [industryId, getPropertyGroupsForIndustry, isMounted])

    /**
     * As soon as the component mounts, fetch the properties
     */
    useEffect(() => {
        void getProperties();
    }, [getProperties])

    const getRelationTypes = useCallback(async () => {
        setLoadingRelationTypes(true);
        const response = await Api.getAllPartRelationTypes();
        if (!isMounted())
            return;
        setLoadingRelationTypes(false);
        if (response?.resultFlag) {
            setRelationTypes(response?.data ?? []);
        }
    }, [isMounted])

    /**
     * As soon as the component mounts, fetch the relation types
     * - if the state does not have the apply relation flag set, do not fetch the relation
     */
    useEffect(() => {
        if (!state?.relation?.apply || !!relationTypes)
            return;
        void getRelationTypes();
    }, [relationTypes, getRelationTypes, state?.relation?.apply])

    /**
     * Toggles the property to copy.
     * @type {(function(*, *, *): void)}
     */
    const togglePropertyToCopy = useCallback((id, val, industryBased) => {
        setState(prevState => ({
            ...prevState,
            ...(industryBased
                ? {
                    industryPropertyIds: val
                        ? [...prevState.industryPropertyIds, id]
                        : prevState.industryPropertyIds.filter(e => e !== id),
                }
                : {
                    propertiesIds: val
                        ? [...prevState.propertiesIds, id]
                        : prevState.propertiesIds.filter(e => e !== id),
                }),
        }))
    }, [])

    return (
        <>
            <Box className={classnames('d-flex flex-column', className)} sx={{gap: 2}}>
                <Box className={'d-flex align-items-center justify-content-center w-100'} sx={{gap: 2}}>
                    <Card
                        variant={'elevation'}
                        sx={{boxShadow: 'none', backgroundColor: 'background.tertiary', padding: 2}}
                        className={'d-flex align-items-center justify-content-center'}
                    >
                        <ProductInformationPopover partNo={sourceProduct?.partNo} iconProps={{sx: {color: 'text.secondary'}}}>
                            <Typography color={'text.secondary'} fontWeight={600}>
                                {sourceProduct?.partNo ?? ""}
                            </Typography>
                        </ProductInformationPopover>

                    </Card>
                    <Box className={'d-flex align-items-center justify-content-center'}>
                        <TrendingFlat color={'tertiary'} sx={{height: 40, width: 60}}/>
                    </Box>
                    <Card
                        variant={'elevation'}
                        sx={{boxShadow: 'none', backgroundColor: 'background.tertiary', padding: 2}}
                        className={'d-flex align-items-center justify-content-center'}
                    >
                        <ProductInformationPopover partNo={targetProduct?.partNo} iconProps={{sx: {color: 'secondary.main'}}}>
                            <Typography color={'secondary.main'} fontWeight={600}>
                                {targetProduct?.partNo ?? ""}
                            </Typography>
                        </ProductInformationPopover>
                    </Card>
                </Box>
                {
                    !copyingInformation && loadingState &&
                    <Box sx={{marginBlock: 10}}>
                        <LoadingLottieAnimation width={'80%'}/>
                    </Box>
                }
                {
                    !copyingInformation && !loadingState &&
                    <>
                        <Grid2 container spacing={2} marginBlock={2}>
                            {
                                loadingProperties &&
                                Array.from({length: 6}).map((_, i) => (
                                    <Grid2 xs={12} md={6} key={`loading-${i}`}>
                                        <div className={'d-flex align-items-center justify-content-between'}>
                                            <Skeleton
                                                width={100}
                                                height={30}
                                                sx={{transform: 'none'}}
                                            />
                                            <Skeleton>
                                                <Switch
                                                    color={'primary'}
                                                    checked={false}
                                                    disabled={copyingInformation}
                                                    onChange={() => void 0}
                                                />
                                            </Skeleton>
                                        </div>
                                    </Grid2>
                                ))
                            }
                            {
                                properties.map((property) => (
                                    <Grid2 xs={12} md={6} key={property.id}>
                                        <div className={'d-flex align-items-center justify-content-between'}>
                                            <Typography variant={'body2'}>{property.title}</Typography>
                                            <Switch
                                                color={'primary'}
                                                checked={
                                                    property.industryBased
                                                        ? state.industryPropertyIds.includes(property.id)
                                                        : state.propertiesIds.includes(property.id)
                                                }
                                                disabled={copyingInformation}
                                                onChange={(val) => togglePropertyToCopy(property.id, val, property.industryBased)}
                                            />
                                        </div>
                                    </Grid2>
                                ))
                            }
                        </Grid2>
                        <Divider orientation={'horizontal'} flexItem sx={{borderStyle: 'dashed', borderWidth: 1}}/>
                        <Box className={'d-flex flex-column'} sx={{gap: 2}}>
                            <Box className={'d-flex align-items-center justify-content-between'}>
                                <Typography variant={'body1'} fontWeight={600}>
                                    Price
                                </Typography>
                                <Switch
                                    color={'primary'}
                                    checked={state.price.apply}
                                    disabled={copyingInformation}
                                    onChange={(val) => setState(prevState => ({
                                        ...prevState,
                                        price: {
                                            ...prevState.price,
                                            apply: val,
                                        }
                                    }))}
                                />
                            </Box>
                            {
                                !!state?.price?.apply &&
                                <>
                                    <Typography variant={'body2'}>
                                        Please determine how you would like the price of the target product to be relative to the source
                                        product.
                                        You can also leave the value as zero for the price to remain the same.
                                    </Typography>
                                    <Box className={'d-flex align-items-center justify-content-end'} sx={{gap: 1}}>
                                        <TextField
                                            select
                                            label={'Type'}
                                            variant={'outlined'}
                                            value={state.price.isPercentage ? "%" : '$'}
                                            disabled={copyingInformation}
                                            sx={{width: 100}}
                                            onChange={(e) => setState(prevState => ({
                                                ...prevState,
                                                price: {
                                                    ...prevState.price,
                                                    isPercentage: e.target.value === "%",
                                                }
                                            }))}
                                        >
                                            <MenuItem value={'$'}>$</MenuItem>
                                            <MenuItem value={'%'}>%</MenuItem>
                                        </TextField>
                                        <TextField
                                            fullWidth
                                            type={'number'}
                                            label={'Value'}
                                            variant={'outlined'}
                                            value={state.price.value?.toString() ?? ''}
                                            disabled={copyingInformation}
                                            onChange={(e) => setState(prevState => ({
                                                ...prevState,
                                                price: {
                                                    ...prevState.price,
                                                    value: e.target.value,
                                                }
                                            }))}
                                            onBlur={(e) => setState(prevState => ({
                                                ...prevState,
                                                price: {
                                                    ...prevState.price,
                                                    value: Number.isNaN(Number(e.target.value)) ? 0 : Number(e.target.value),
                                                }
                                            }))}
                                        />
                                    </Box>
                                </>
                            }
                        </Box>
                        <Divider orientation={'horizontal'} flexItem sx={{borderStyle: 'dashed', borderWidth: 1}}/>
                        <Box className={'d-flex flex-column'} sx={{gap: 2}}>
                            <Box className={'d-flex align-items-center justify-content-between'}>
                                <Typography variant={'body1'} fontWeight={600}>
                                    Relation
                                </Typography>
                                <Switch
                                    color={'primary'}
                                    checked={state.relation.apply}
                                    disabled={copyingInformation}
                                    onChange={(val) => setState(prevState => ({
                                        ...prevState,
                                        relation: {
                                            ...prevState.relation,
                                            apply: val,
                                        }
                                    }))}
                                />
                            </Box>
                            {
                                !!state?.relation?.apply &&
                                <>
                                    <Typography variant={'body2'}>
                                        You may want to make both products related to each other. Please select the type of relation you
                                        would like to be applied for both products.
                                    </Typography>
                                    <Box className={'d-flex align-items-center justify-content-end'} sx={{gap: 1}}>
                                        <TextField
                                            select
                                            fullWidth
                                            label={'Relation Type'}
                                            variant={'outlined'}
                                            value={state.relation.relationType}
                                            disabled={copyingInformation || loadingRelationTypes}
                                            SelectProps={{
                                                renderValue: (value) => value?.title ?? '',
                                                endAdornment: loadingRelationTypes && <CircularProgress color={'secondary'} size={20}/>,
                                            }}
                                            onChange={(e) => setState(prevState => ({
                                                ...prevState,
                                                relation: {
                                                    ...prevState.relation,
                                                    relationType: e.target.value,
                                                }
                                            }))}
                                        >
                                            {
                                                relationTypes?.map((type) => (
                                                    <MenuItem key={type.id} value={type}>
                                                        {type.title}
                                                    </MenuItem>
                                                ))
                                            }
                                        </TextField>
                                    </Box>
                                </>
                            }
                        </Box>
                        {
                            mode !== CopyProductDialogModes.makeCopy &&
                            <>
                                <Divider orientation={'horizontal'} flexItem sx={{borderStyle: 'dashed', borderWidth: 1}}/>
                                <Box className={'d-flex flex-column'} sx={{gap: 1}}>
                                    <Box className={'d-flex align-items-center justify-content-between'}>
                                        <Box className={'d-flex align-items-center'}>
                                            <Typography variant={'body1'} fontWeight={600}>
                                                Hard Copy
                                            </Typography>
                                        </Box>
                                        <Switch
                                            color={'primary'}
                                            checked={state.hardCopy}
                                            disabled={copyingInformation}
                                            onChange={(val) => setState(prevState => ({
                                                ...prevState,
                                                hardCopy: val,
                                            }))}
                                        />
                                    </Box>
                                    {
                                        !!state?.hardCopy &&
                                        <Typography variant={'body2'}>
                                            This Will remove the current information of
                                            <span
                                                className={'mx-1 secondary-color-1 font-weight-semi-bold'}>{sourceProduct?.partNo ?? ""}</span>
                                            and replace it with the
                                            <span
                                                className={'mx-1 secondary-color-1 font-weight-semi-bold'}>{targetProduct?.partNo ?? ""}</span>
                                            information from
                                        </Typography>
                                    }
                                </Box>
                            </>
                        }
                        <Divider orientation={'horizontal'} flexItem sx={{borderStyle: 'dashed', borderWidth: 1}}/>
                        <Box className={'d-flex'} sx={{gap: 2}}>
                            <Checkbox
                                label={'Set as Default Settings'}
                                checked={hasDefaultSettings}
                                onChange={() => setHasDefaultSettings(prevState => !prevState)}
                            />
                        </Box>
                    </>
                }
                {
                    copyingInformation &&
                    <div className={'d-flex flex-column'}>
                        <ImportingLottieAnimation width={'80%'}/>
                        <Typography variant={'body2'}>
                            We are copying the information from the selected product. This may take a few seconds.
                        </Typography>
                    </div>
                }
            </Box>
        </>
    );
})

const ReviewResults = ({copyResults, sourceProduct, targetProduct, className, close}) => {

    const resultsSummaryInfo = useMemo(() => ({
        header: {
            title: 'Copy Results',
            value: "Successful",
            loadingValue: "Loading...",
        },
        body: [
            {
                id: "newDescriptionAdded",
                title: 'New Description Added',
                value: copyResults?.newDescriptionAdded,
                format: () => copyResults?.newDescriptionAdded ? "Yes" : "No",
            },
            {
                id: "copiedPropertiesCount",
                title: 'Copied Tags',
                value: copyResults?.copiedTagsCount,
                format: () => copyResults?.copiedTagsCount ?? 0,
            },
            {
                id: "copiedTechnicalProperties",
                title: 'Copied Technical Properties',
                value: copyResults?.copiedTechnicalProperties,
                format: () => copyResults?.copiedTechnicalProperties ?? 0,
            },
            {
                id: "copiedImages",
                title: 'Copied Images',
                value: copyResults?.copiedImages,
                format: () => copyResults?.copiedImages ?? 0,
            },
            {
                id: "copiedCodes",
                title: 'Copied Codes',
                value: copyResults?.copiedCodes,
                format: () => copyResults?.copiedCodes ?? 0,
            },
            {
                id: "copiedTechnicalFiles",
                title: 'Copied Technical Files',
                value: copyResults?.copiedTechnicalFiles,
                format: () => copyResults?.copiedTechnicalFiles ?? 0,
            },
            {
                id: "copiedNotes",
                title: 'Copied Notes',
                value: copyResults?.copiedNotes,
                format: () => copyResults?.copiedNotes ?? 0,
            },
            {
                id: "copiedGroupProperties",
                title: 'Copied Group Properties',
                value: copyResults?.copiedGroupProperties,
                format: () => copyResults?.copiedGroupProperties ?? 0,
            },
            {
                id: "priceCopied",
                title: 'Price Copied',
                value: copyResults?.priceCopied,
                format: () => copyResults?.priceCopied ? "Yes" : "No",
            },
            {
                id: "priceCopiedErrorMessage",
                title: 'Price Copied Error Message',
                value: copyResults?.priceCopiedErrorMessage,
                format: () => copyResults?.priceCopiedErrorMessage ?? "N/A",
            },
        ],
    }), [copyResults])

    return (
        <>
            <Box className={classnames('d-flex flex-column', className)} sx={{gap: 2}}>
                <Box className={'d-flex align-items-center justify-content-center w-100'} sx={{gap: 2}}>
                    <Card
                        variant={'elevation'}
                        sx={{boxShadow: 'none', backgroundColor: 'background.tertiary', padding: 2, gap: 1}}
                        className={'d-flex align-items-center justify-content-center'}
                    >
                        <ProductInformationPopover partNo={sourceProduct?.partNo} iconProps={{sx: {color: 'text.secondary'}}}>
                            <Typography color={'text.secondary '} fontWeight={600}>
                                {sourceProduct?.partNo ?? ""}
                            </Typography>
                        </ProductInformationPopover>
                        <span>
                            <IconButton
                                color={'paper.primary'}
                                component={Link}
                                target={'_blank'}
                                to={routeFunctions.products.single(sourceProduct?.id)}
                                sx={{display: 'inline-block', marginInlineStart: 0.5}}
                            >
                                <OpenInNew/>
                            </IconButton>
                        </span>
                    </Card>
                    <Box className={'d-flex align-items-center justify-content-center'}>
                        <TrendingFlat color={'tertiary'} sx={{height: 40, width: 60}}/>
                    </Box>
                    <Card
                        variant={'elevation'}
                        sx={{boxShadow: 'none', backgroundColor: 'background.tertiary', padding: 2, gap: 1}}
                        className={'d-flex align-items-center justify-content-center'}
                    >
                        <ProductInformationPopover partNo={targetProduct?.partNo} iconProps={{sx: {color: 'secondary.main'}}}>
                            <Typography color={'secondary.main'} fontWeight={600}>
                                {targetProduct?.partNo ?? ""}
                            </Typography>
                        </ProductInformationPopover>
                        <span>
                            <IconButton
                                color={'paper.primary'}
                                component={Link}
                                target={'_blank'}
                                to={routeFunctions.products.single(targetProduct?.id)}
                                sx={{display: 'inline-block', marginInlineStart: 0.5}}
                            >
                                <OpenInNew/>
                            </IconButton>
                        </span>
                    </Card>
                </Box>
                <SummaryInfoCard
                    header={resultsSummaryInfo.header}
                    body={resultsSummaryInfo.body}
                    loadingCount={resultsSummaryInfo.body.length}
                />
                <Box className={'d-flex align-items-center justify-content-center'}>
                    <Button
                        className={'px-5 py-2-5'}
                        variant={'contained'}
                        color={'primary'}
                        onClick={close}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </>
    );
}


export default CopyProductDialog;
